import { Button } from "../../../components/buttons/button";
import Dropdown from "../../../components/dropdown/dropdown";
import { SearchInput } from "../../../components/inputs/searchInput";
import { PageHeader } from "../../../components/typography/pageHeader";
import { BgWrapper } from "../../../components/wrapper/bgWrapper";
import { useModalControl } from "../../../customeHooks/useModalControl";
import { AddAdminModal } from "../../../components/modal/app_modals/addAdminModal";
import { SettingsLayout } from "../../../components/layout/settingsLayout";
import { useState } from "react";
import { QueryWithStatus } from "../../../utils/types";
import { useGetAllAdminsQuery } from "../../../redux/features/admin/admin.api";
import { ProfileIcon } from "../../../components/cards/profilePic";
import { TableComponent } from "../../../components/table";
import { Pill } from "../../../components/pill";
import { Icon } from "../../../components/icon/icon";
import { DeleteAdminModal } from "../../../components/modal/app_modals/deleteAdminModal";
import { useAppSelector } from "../../../redux/hooks";
import { ChangeAdminRole } from "../../../components/modal/app_modals/changeAdminRole";

const actions = [
  {
    label: "Change Role",
    icon: "refresh",
    modal: "changeRoleModal",
    className:
      "flex items-center font-bold text-grey-950 gap-2 px-4 py-2 text-sm hover:bg-grey-50",
  },
  {
    label: "Delete",
    icon: "delete",
    modal: "deleteAdminModal",
    className:
      "flex items-center font-bold gap-2 px-4 py-2 text-sm text-danger hover:bg-grey-50",
  },
];

export const SettingsPage = () => {
  const [query, setQuery] = useState<QueryWithStatus>({
    page: 1,
    limit: 50,
    search: undefined,
    sortOrder: "desc",
    status: undefined,
  });
  const { modal, modalOpen, handleModalOpen, handleModalClose } =
    useModalControl();
  const { adminProfile } = useAppSelector((state) => state.auth);

  const [selectedAdmin, setSelectedAdmin] = useState<{
    _id: string;
    name: string;
    role: string;
  } | null>(null);

  const {
    isFetching: fetchingAdmins,
    data: adminData,
    refetch: refetchAdmin,
  } = useGetAllAdminsQuery(
    { ...query, search: query.search === "" ? undefined : query.search },
    {
      refetchOnMountOrArgChange: true,
    },
  );
  // Reorder admins so logged-in admin always appears first
  const orderedAdmins = adminData?.data?.data
    ? [
        ...adminData.data.data.filter(
          (admin) => admin._id === adminProfile?._id,
        ),
        ...adminData.data.data.filter(
          (admin) => admin._id !== adminProfile?._id,
        ),
      ]
    : [];

  return (
    <div className="w-full md:flex md:h-full md:flex-col">
      <PageHeader header={"Settings"} className="font-dela font-normal" />
      <main className="mt-10 flex flex-col items-start gap-[30px] md:min-h-0 md:flex-1 md:flex-row md:gap-[80px]">
        <SettingsLayout />
        <div className="flex w-full flex-col gap-[2px] md:min-h-0 md:flex-1 md:self-stretch">
          <div className="flex h-[58px] cursor-pointer items-center justify-between rounded-2xl bg-white p-3">
            <h1 className="font-fustat text-base font-bold text-grey-950">
              {adminData?.data?.totalCount ?? 0} Admins
            </h1>
            {adminProfile?.role === "super-admin" && (
              <Button
                text="Add Admin"
                icon="plus"
                type="button"
                onClick={() => {
                  handleModalOpen("addAdminModal");
                }}
                variation="blue"
                iconClassName={"mb-1"}
                className="max-w-fit"
              />
            )}
          </div>
          <BgWrapper className="flex flex-col gap-3 rounded-t-2xl md:min-h-0 md:flex-1">
            <div className="flex w-full flex-col items-center justify-between gap-4 md:flex-row">
              <SearchInput
                searchFunction={(value) => {
                  setQuery({
                    ...query,
                    search: value,
                    page: 1,
                  });
                }}
                placeholder="Search"
                className="md:!max-w-[300px]"
              />
            </div>
            <TableComponent
              headers={[]}
              rows={
                orderedAdmins?.map((admin, idx) => {
                  const isLoggedIn = admin._id === adminProfile?._id;
                  const canManage =
                    adminProfile?.role === "super-admin" &&
                    admin._id !== adminProfile?._id;
                  return {
                    id: idx,
                    items: [
                      <div
                        className={`flex w-full flex-1 flex-col gap-2 md:flex-row md:items-center`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <ProfileIcon img="" dimension={32} className="" />
                        </div>

                        <p className="flex flex-1 flex-wrap items-center gap-0 text-sm text-grey-600">
                          <span className="font-bold text-grey-900">
                            {admin.firstName} {admin.lastName}{" "}
                            {isLoggedIn && "(You)"}
                          </span>
                          <span className={` `}>∙</span>
                          <span className={``}>{admin.email}</span>
                        </p>

                        <div className="flex flex-row flex-wrap items-center gap-2">
                          {admin.role === "super-admin" && (
                            <Pill text={"SUPER ADMIN"} variation={"purple"} />
                          )}
                          {admin.role === "admin" && (
                            <Pill text={"ADMIN"} variation={"green"} />
                          )}
                          {canManage && (
                            <Dropdown
                              position="bottom end"
                              customMenuItems={() => {
                                return (
                                  <div className="flex flex-col rounded-xl border border-grey-200 bg-white">
                                    {actions.map((action) => (
                                      <button
                                        key={action.label}
                                        onClick={() => {
                                          setSelectedAdmin({
                                            _id: admin._id,
                                            name: `${admin.firstName} ${admin.lastName}`,
                                            role: admin?.role,
                                          });
                                          handleModalOpen(action.modal);
                                        }}
                                        className={action.className}
                                      >
                                        <Icon
                                          svg={action.icon}
                                          width={16}
                                          height={17}
                                          className="mb-1"
                                        />
                                        {action.label}
                                      </button>
                                    ))}
                                  </div>
                                );
                              }}
                            >
                              <p className="cursor-pointer rounded-full px-[9px] py-[7px] text-primary hover:bg-grey-50">
                                <Icon svg="ellipsis" width={20} height={20} />
                              </p>
                            </Dropdown>
                          )}
                        </div>
                      </div>,
                    ],
                  };
                }) ?? []
              }
              loadingRows={query.limit}
              isLoading={fetchingAdmins}
              isRowClickable={false}
              isTableEmpty={[0, undefined].includes(
                adminData?.data?.data?.length,
              )}
              emptyTitle="No Admin"
              emptySubTitle=""
              emptyImage="/assets/images/guest-glass.png"
              isPaginated
              currentPage={query.page ?? 1}
              totalCount={adminData?.data?.totalCount ?? 0}
              itemsPerPage={query.limit!}
              pageRangeDisplayed={3}
              onPageChange={(page) => {
                setQuery({
                  ...query,
                  page: page,
                });
              }}
              className=""
            />
          </BgWrapper>
        </div>
      </main>
      {modalOpen && (
        <AddAdminModal
          open={modal === "addAdminModal" && modalOpen}
          handleClose={() => {
            handleModalClose();
          }}
          refetchAdmin={refetchAdmin}
          onSuccess={() => {
            console.log("success");
          }}
        />
      )}

      {modalOpen && (
        <DeleteAdminModal
          open={modal === "deleteAdminModal" && modalOpen}
          handleClose={() => {
            handleModalClose();
            setSelectedAdmin(null);
          }}
          onSuccess={() => {
            console.log("success");
            setSelectedAdmin(null);
          }}
          refetchAdmin={refetchAdmin}
          adminId={selectedAdmin?._id ?? ""}
          adminName={selectedAdmin?.name ?? ""}
        />
      )}
      {modalOpen && (
        <ChangeAdminRole
          refetchAdmin={refetchAdmin}
          adminId={selectedAdmin?._id ?? ""}
          adminRole={selectedAdmin?.role ?? ""}
          open={modal === "changeRoleModal" && modalOpen}
          handleClose={() => {
            handleModalClose();
            setSelectedAdmin(null);
          }}
          onSuccess={() => {
            console.log("success");
            setSelectedAdmin(null);
          }}
        />
      )}
    </div>
  );
};
